import { useAuth as useClerkAuth, useClerk } from '@clerk/clerk-react';
import { type ReactElement, useCallback, useEffect, useMemo } from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { RecoveryStatusPanel } from '@/auth/recoveryUx';
import { hasClerkClientSession } from '@/auth/useAuth.helpers';
import { AuthErrorBoundary } from '@/components/AuthErrorBoundary';
import {
  buildProjectScopedPath,
  isProjectScopedProductPath,
  stripProjectRouteScope,
} from '@/features/projects/utils/projectScopedRoutes';
import { buildBreadcrumbItems } from '@/navigation/breadcrumbs';
import { AppRoutes } from '@/navigation/routes/AppRoutes';
import { useAppRouting } from '@/shared/hooks';
import { useAppAuthBootstrapFlow } from '@/shared/hooks/useAppAuthBootstrapFlow';
import { shouldDelayProjectRouteRenderForContext } from './appBootstrapRouteContext';
import type { AppRouteDefinition } from '../navigation/routes/routes';
import type { AuthError, AuthSession } from '@/auth/types';
import type { BreadcrumbItem } from '@/ui/layout/breadcrumbsTypes';
import type { OrganisationDto, ProjectDto } from '@flowqa/shared';

type AppDialogsState = {
  addProjectDialog: { open: boolean; onboarding: boolean };
  openAddProject: (setAsCurrent: boolean, options?: { onboarding?: boolean }) => void;
  dismissAll: () => void;
};

type AppBootstrapData = {
  projectsLoaded: boolean;
  projects: ProjectDto[];
  visibleProjectOptions: Array<
    Pick<
      ProjectDto,
      'id' | 'name' | 'slug' | 'organisationId' | 'badgeChars' | 'badgeColor' | 'badgeEnabled'
    >
  >;
};

export type AdminAccessState = {
  canAccess: boolean;
  loading: boolean;
};

type AppBootstrapControllerProps = {
  authBypassClerk: boolean;
  session: AuthSession | null;
  authLoading: boolean;
  authError: AuthError | null;
  signIn: (input: { email: string; password: string }) => Promise<void>;
  signUp: (input: {
    email: string;
    password: string;
    firstName?: string;
    lastName?: string;
    acceptedPolicies: boolean;
  }) => Promise<void>;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<boolean>;
  setSessionDirect: (next: AuthSession | null) => void;
  adminAccess: AdminAccessState;
  isSuperuser: boolean;
  organisations: Array<Pick<OrganisationDto, 'id' | 'name' | 'slug'>>;
  bootstrap: AppBootstrapData;
  selectedOrganisationId: string | null;
  selectedProjectId: string | null;
  setSelectedOrganisationId: (value: string | null) => void;
  setSelectedProjectId: (value: string | null) => void;
  dialogs: AppDialogsState;
  routeDefinitions: AppRouteDefinition[];
  featureFlags: Record<string, boolean> | null;
  effectiveFeatureFlags: Record<string, boolean> | null;
  effectiveFlagsLoading: boolean;
  featureFlagsLoading: boolean;
  canCreateProject: boolean;
  storedProjectId: string | null;
  sessionLastOrganisationId: string | null;
  sessionLastProjectId: string | null;
  onRouteAddProject: () => void;
  updateUserContext: (context: {
    organisationId?: string | null;
    projectId?: string | null;
    lastOrganisationId?: string | null;
    lastProjectId?: string | null;
  }) => void;
  children: (state: AppBootstrapControllerState) => ReactElement;
};

type AppBootstrapControllerInnerProps = AppBootstrapControllerProps & {
  hasRecoverableSessionSignal: boolean;
};

const isDetailRoutePath = (activePath: string, routeDefinitions: AppRouteDefinition[]): boolean =>
  routeDefinitions.some((route) => {
    if (!route.key.includes('detail')) return false;
    return matchPath({ path: route.path, end: true }, activePath) !== null;
  });

export type AppBootstrapControllerState = {
  activePath: string;
  isEntryAuthFlow: boolean;
  onboardingIncomplete: boolean;
  showAuthenticatedShell: boolean;
  sidebarFeatureFlags: Record<string, boolean>;
  routes: ReactElement;
  breadcrumbItems: BreadcrumbItem[];
  showBreadcrumbs: boolean;
  restrictTopBarToSignOut: boolean;
};

export const AppBootstrapController = (props: AppBootstrapControllerProps): ReactElement => {
  if (props.authBypassClerk) {
    return <AppBootstrapControllerInner {...props} hasRecoverableSessionSignal={false} />;
  }

  return <AppBootstrapControllerWithClerk {...props} />;
};

const AppBootstrapControllerWithClerk = (props: AppBootstrapControllerProps): ReactElement => {
  const { isSignedIn: isClerkSignedIn } = useClerkAuth();
  const { client } = useClerk();
  const hasRecoverableSessionSignal = [
    isClerkSignedIn,
    hasClerkClientSession(client as unknown),
  ].some((value) => value);

  return (
    <AppBootstrapControllerInner
      {...props}
      hasRecoverableSessionSignal={hasRecoverableSessionSignal}
    />
  );
};

const AppBootstrapControllerInner = ({
  authBypassClerk,
  session,
  authLoading,
  authError,
  signIn,
  signUp,
  signOut,
  refreshSession,
  setSessionDirect,
  adminAccess,
  isSuperuser,
  organisations,
  bootstrap,
  selectedOrganisationId,
  selectedProjectId,
  setSelectedOrganisationId,
  setSelectedProjectId,
  dialogs,
  routeDefinitions,
  featureFlags,
  effectiveFeatureFlags,
  effectiveFlagsLoading,
  featureFlagsLoading: _featureFlagsLoading,
  canCreateProject,
  storedProjectId,
  sessionLastOrganisationId,
  sessionLastProjectId,
  onRouteAddProject,
  updateUserContext,
  children,
  hasRecoverableSessionSignal,
}: AppBootstrapControllerInnerProps): ReactElement => {
  const location = useLocation();
  const navigate = useNavigate();
  const activePath = location.pathname;
  const isAddProjectDialogOpen = dialogs.addProjectDialog.open;
  const isAddProjectOnboarding = dialogs.addProjectDialog.onboarding;
  const dismissAllDialogs = dialogs.dismissAll;
  const openAddProjectDialog = dialogs.openAddProject;

  const handleManualSignOut = useCallback((): void => {
    void signOut();
    void navigate('/sign-in', { replace: true, state: { manualSignOut: true } });
  }, [navigate, signOut]);

  const {
    isEntryAuthFlow,
    authBootstrapState,
    shouldBlockRoutesDuringRecovery,
    recoveryPhase,
    onboardingIncomplete,
    showAuthenticatedShell,
  } = useAppAuthBootstrapFlow({
    session,
    authLoading,
    authError,
    activePath,
    routeDefinitions,
    navigate,
    hasRecoverableSessionSignal,
  });

  useAppRouting({
    authLoading,
    bootstrapLoaded: bootstrap.projectsLoaded,
    session,
    pathname: activePath,
    organisations,
    projects: bootstrap.projects,
    selectedOrganisationId,
    selectedProjectId,
    setSelectedOrganisationId,
    setSelectedProjectId,
    navigate,
    updateUserContext,
    allowOrganisationRouting: !onboardingIncomplete,
  });

  useEffect(() => {
    if (authLoading) return;
    if (!session) return;
    if (!bootstrap.projectsLoaded) return;
    if (activePath !== stripProjectRouteScope(activePath)) return;
    if (!isProjectScopedProductPath(activePath)) return;
    const selectedProject =
      selectedProjectId === null
        ? null
        : (bootstrap.projects.find((project) => project.id === selectedProjectId) ?? null);
    if (!selectedProject) return;
    if (selectedProject.organisationId === null) return;
    const workspaceSlug =
      organisations.find((organisation) => organisation.id === selectedProject.organisationId)
        ?.slug ?? null;
    if (workspaceSlug === null) return;
    void navigate(
      `${buildProjectScopedPath({
        workspaceSlug,
        projectSlug: selectedProject.slug,
        path: activePath,
      })}${location.search}${location.hash}`,
      { replace: true },
    );
  }, [
    activePath,
    authLoading,
    bootstrap.projects,
    bootstrap.projectsLoaded,
    location.hash,
    location.search,
    navigate,
    organisations,
    selectedProjectId,
    session,
  ]);

  useEffect(() => {
    if (!isEntryAuthFlow) return;
    dismissAllDialogs();
  }, [dismissAllDialogs, isEntryAuthFlow]);

  useEffect(() => {
    if (authLoading) return;
    if (!session) return;
    if (session.onboardingCompleted) return;
    if (onboardingIncomplete) return;
    if (!bootstrap.projectsLoaded) return;
    if (activePath !== '/execution-runs') return;
    if (organisations.length > 0 && selectedOrganisationId === null) return;
    if (isEntryAuthFlow) return;
    if (isAddProjectDialogOpen) return;
    if (!canCreateProject) return;
    if (selectedProjectId) return;
    if (storedProjectId) return;
    if (sessionLastProjectId) return;
    if (sessionLastOrganisationId && selectedOrganisationId !== sessionLastOrganisationId) return;
    if (bootstrap.projects.length > 0) return;
    if (bootstrap.visibleProjectOptions.length > 0) return;
    openAddProjectDialog(true, { onboarding: true });
  }, [
    activePath,
    authLoading,
    bootstrap.projects,
    bootstrap.projectsLoaded,
    bootstrap.visibleProjectOptions.length,
    canCreateProject,
    openAddProjectDialog,
    isAddProjectDialogOpen,
    isEntryAuthFlow,
    onboardingIncomplete,
    organisations.length,
    selectedOrganisationId,
    selectedProjectId,
    session,
    sessionLastOrganisationId,
    sessionLastProjectId,
    storedProjectId,
  ]);

  useEffect(() => {
    if (activePath !== '/onboarding') return;
    if (!isAddProjectDialogOpen) return;
    dismissAllDialogs();
  }, [activePath, dismissAllDialogs, isAddProjectDialogOpen]);

  const defaultRoutes = (
    <AuthErrorBoundary key={activePath} session={session} onSignOut={handleManualSignOut}>
      <AppRoutes
        routeDefinitions={routeDefinitions}
        featureFlagsLoading={effectiveFlagsLoading}
        featureFlags={effectiveFeatureFlags ?? featureFlags}
        authBypassClerk={authBypassClerk}
        session={session}
        authLoading={authLoading}
        projectsLoaded={bootstrap.projectsLoaded}
        authError={authError}
        signIn={signIn}
        signUp={signUp}
        signOut={signOut}
        refreshSession={refreshSession}
        setSessionDirect={setSessionDirect}
        isAdmin={adminAccess.canAccess}
        isSuperuser={isSuperuser}
        adminAccessLoading={adminAccess.loading}
        organisations={organisations}
        projects={bootstrap.projects}
        onAddProject={onRouteAddProject}
      />
    </AuthErrorBoundary>
  );

  const delayProjectRouteRenderForContext = shouldDelayProjectRouteRenderForContext({
    activePath,
    authLoading,
    session,
    projectsLoaded: bootstrap.projectsLoaded,
    organisations,
    projects: bootstrap.projects,
    selectedOrganisationId,
    selectedProjectId,
    routeDefinitions,
  });

  const routes = shouldBlockRoutesDuringRecovery ? (
    <RecoveryStatusPanel
      phase={authBootstrapState === 'error_terminal' ? 'stalled' : recoveryPhase}
    />
  ) : delayProjectRouteRenderForContext ? (
    <></>
  ) : (
    defaultRoutes
  );

  const breadcrumbItems = useMemo(
    () => buildBreadcrumbItems(activePath, routeDefinitions),
    [activePath, routeDefinitions],
  );
  const hideBreadcrumbsOnSettings =
    /^\/workspace-settings$/.test(activePath) ||
    /^\/project-settings$/.test(activePath) ||
    /^\/workspaces\/[^/]+\/projects\/[^/]+\/settings$/.test(activePath);
  const hideBreadcrumbsOnProjectScopedProductPath = isProjectScopedProductPath(activePath);
  const hideBreadcrumbsOnDetail = isDetailRoutePath(activePath, routeDefinitions);
  const showBreadcrumbs =
    activePath !== '/' &&
    !hideBreadcrumbsOnSettings &&
    !hideBreadcrumbsOnProjectScopedProductPath &&
    !hideBreadcrumbsOnDetail;

  const sidebarFeatureFlags = useMemo(() => {
    if (!effectiveFlagsLoading) return effectiveFeatureFlags ?? featureFlags ?? {};
    return {
      organisations: false,
    };
  }, [effectiveFeatureFlags, effectiveFlagsLoading, featureFlags]);

  return children({
    activePath,
    isEntryAuthFlow,
    onboardingIncomplete,
    showAuthenticatedShell,
    sidebarFeatureFlags,
    routes,
    breadcrumbItems,
    showBreadcrumbs,
    restrictTopBarToSignOut: isAddProjectDialogOpen && isAddProjectOnboarding,
  });
};
